import React, {
	useEffect,
	useMemo,
	useState,
	useRef,
	useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../data/store';
import {
	fetchUserGroups,
	deleteUserGroup,
} from '../../data/thunks/userGroupThunk';
import { updateUserGroupsFilter } from '../../data/stores/userGroupStore';
import usePagination from '../../hooks/usePaginationStore';
import Table from '../common/Table';
import { GridColDef } from '@mui/x-data-grid';
import { IconButton, Typography, Box, Tooltip } from '@mui/material';
import { styled } from 'styled-components';
import ScreenBusySpinner from '../common/ScreenBusySpinner';
import SearchBar from '../common/SearchBar';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { UserGroup } from '../../data/models/userGroup';
import EditGroupModal from './EditGroupModal';
import Dialog, {
	DialogContent,
	DialogTitle,
	DialogActions,
} from '../common/Dialog';
import Button from '../common/buttons/Button';

export default function Groups() {
	const dispatch: AppDispatch = useDispatch();

	const { userGroups } = useSelector(
		(state: RootState) => state.userGroupState
	);
	const { company } = useSelector((state: RootState) => state.companyState);
	const { openId } = useSelector((state: RootState) => state.modalState);

	const [searchText, setSearchText] = useState('');
	const isLoadingRef = useRef(false);
	const [openEditModal, setOpenEditModal] = useState(false);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [selectedGroup, setSelectedGroup] = useState<UserGroup | undefined>(
		undefined
	);

	const { page, setPage, pageSize, sortModel, setSortModel } = usePagination(
		'userGroupState',
		'userGroups',
		updateUserGroupsFilter
	);

	const filterParams = useMemo(
		() => ({
			companyId: company?.id,
			searchText: searchText,
			order:
				(sortModel && sortModel.length > 0 && sortModel[0]?.field) ||
				'created',
			direction:
				(sortModel && sortModel.length > 0 && sortModel[0]?.sort) ||
				'desc',
			page: page,
			take: pageSize,
		}),
		[
			company?.id,
			searchText,
			page,
			pageSize,
			sortModel?.[0]?.field,
			sortModel?.[0]?.sort,
		]
	);

	useEffect(() => {
		if (company?.id && !isLoadingRef.current) {
			isLoadingRef.current = true;
			dispatch(fetchUserGroups(filterParams)).finally(() => {
				isLoadingRef.current = false;
			});
		}
	}, [dispatch, filterParams]);

	const handleTextSearch = (newSearchText: string) => {
		setSearchText(newSearchText);
		setPage(0);
	};

	const handleSortOnChange = (order) => {
		setSortModel(order);
	};

	const handleOnPageChange = (newPage: number, scrollToTop = false) => {
		setPage(newPage);
		if (scrollToTop) {
			window.scrollTo({ top: 0, behavior: 'smooth' });
		}
	};

	const handleEdit = (group: UserGroup) => {
		setSelectedGroup(group);
		setOpenEditModal(true);
	};

	const handleDelete = (group: UserGroup) => {
		setSelectedGroup(group);
		setOpenDeleteDialog(true);
	};

	const handleConfirmDelete = async () => {
		if (selectedGroup?.id) {
			await dispatch(deleteUserGroup(selectedGroup.id));
			// Refresh the list
			dispatch(fetchUserGroups(filterParams));
			setOpenDeleteDialog(false);
			setSelectedGroup(undefined);
		}
	};

	const prevOpenIdRef = useRef<string>('');

	const handleModalClose = useCallback(() => {
		// Refresh the list when modals close
		dispatch(fetchUserGroups(filterParams));
	}, [dispatch, filterParams]);

	useEffect(() => {
		// Refresh the list when the modal closes (transitions from open to closed)
		if (
			prevOpenIdRef.current === 'groupCreationModal' &&
			openId !== 'groupCreationModal'
		) {
			handleModalClose();
		}
		prevOpenIdRef.current = openId;
	}, [openId, handleModalClose]);

	const renderActionButtons = (group: UserGroup) => {
		return (
			<ActionsContainer>
				<Tooltip title="Edit">
					<IconButton
						onClick={() => handleEdit(group)}
						color="primary"
						size="small"
					>
						<EditIcon fontSize="small" />
					</IconButton>
				</Tooltip>
				<Tooltip title="Delete">
					<IconButton
						onClick={() => handleDelete(group)}
						color="error"
						size="small"
					>
						<DeleteIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			</ActionsContainer>
		);
	};

	const columns: GridColDef[] = useMemo(() => {
		return [
			{
				field: 'name',
				headerName: 'Group Name',
				minWidth: 250,
				disableColumnMenu: true,
				renderCell: (params) => (
					<GroupNameCell onClick={() => handleEdit(params.row)}>
						<ClickableName>{params.value}</ClickableName>
					</GroupNameCell>
				),
			},
			{
				field: 'totalMembers',
				headerName: 'Number of People',
				minWidth: 150,
				disableColumnMenu: true,
				align: 'left',
				headerAlign: 'left',
				renderCell: (params) => {
					const group = params.row as UserGroup;
					const totalMembers =
						(group.users?.length || 0) +
						(group.externalUsers?.length || 0);
					return totalMembers;
				},
			},
			{
				field: 'alwaysAddToProjects',
				headerName: 'Always Add to Projects',
				minWidth: 200,
				disableColumnMenu: true,
				align: 'left',
				headerAlign: 'left',
				renderCell: (params) => (
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'flex-start',
							alignItems: 'center',
							height: '100%',
						}}
					>
						{params.value ? <CheckIcon /> : <CloseIcon />}
					</Box>
				),
			},
			{
				field: 'actions',
				headerName: '',
				width: 120,
				disableColumnMenu: true,
				align: 'center',
				headerAlign: 'center',
				renderCell: (params) => (
					<div
						style={{
							display: 'flex',
							justifyContent: 'center',
							height: '100%',
						}}
					>
						{renderActionButtons(params.row)}
					</div>
				),
			},
		];
	}, []);

	const rows = useMemo(() => {
		if (
			!userGroups ||
			!userGroups.list ||
			!Array.isArray(userGroups.list)
		) {
			return [];
		}
		return userGroups.list.map((group) => ({
			...group,
			// Add a computed field for "Always Add to Projects" - this could come from backend
			alwaysAddToProjects: false, // TODO: This should come from the actual group data
		}));
	}, [userGroups.list]);

	return (
		<div>
			<ScreenBusySpinner />
			<EditGroupModal
				open={openEditModal}
				handleClose={() => {
					setOpenEditModal(false);
					setSelectedGroup(undefined);
					handleModalClose();
				}}
				group={selectedGroup}
			/>

			{/* Delete Confirmation Dialog */}
			<Dialog
				open={openDeleteDialog}
				handleClose={() => setOpenDeleteDialog(false)}
			>
				<DialogTitle>Delete Group</DialogTitle>
				<DialogContent>
					<Typography>
						Are you sure you want to delete the group "
						<strong>{selectedGroup?.name}</strong>"? This action
						cannot be undone.
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setOpenDeleteDialog(false)}
						variant="outlined"
					>
						Cancel
					</Button>
					<Button onClick={handleConfirmDelete} color="error">
						Delete
					</Button>
				</DialogActions>
			</Dialog>

			{/* Header with search and actions */}
			<HeaderContainer>
				<SearchContainer>
					<SearchBar
						searchText={searchText}
						handleTextSearch={handleTextSearch}
						setSearchText={setSearchText}
						handleClear={() => setSearchText('')}
					/>
				</SearchContainer>
			</HeaderContainer>

			<Table
				columns={columns}
				rows={rows}
				page={page}
				pageSize={pageSize}
				rowCount={userGroups?.count || 0}
				sortModel={sortModel}
				onSortModelChange={handleSortOnChange}
				onPaginationModelChange={handleOnPageChange}
				loading={!userGroups?.initialized}
				noRowsContent={'Click the + New button to add a Group'}
			/>
		</div>
	);
}

const HeaderContainer = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-top: 16px;
	margin-bottom: 16px;
	gap: 16px;
`;

const SearchContainer = styled.div`
	display: flex;
	gap: 12px;
	align-items: center;
`;

const GroupNameCell = styled.div`
	display: flex;
	align-items: center;
	cursor: pointer;
	&:hover {
		opacity: 0.8;
	}
`;

const ClickableName = styled.span`
	color: ${({ theme }) => theme.palette.primary.main};
	text-decoration: none;
	&:hover {
		text-decoration: underline;
	}
`;

const ActionsContainer = styled.div`
	display: flex;
	gap: 4px;
	align-items: center;
	justify-content: center;
`;
